import { useCenterReviewDashboard, useCoachReviewDashboard } from '@/features/centerReview';
import { getItemWithExpiration, Row } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import FeedInstructorReviewsHeader from './FeedInstructorReviewsHeader';
import FeedInstructorReviewsCenterSummary from './FeedInstructorReviewsCenterSummary';
import FeedInstructorReviewsCoachSummary from './FeedInstructorReviewsCoachSummary';
import FeedInstructorReviewsCoachRankings from './FeedInstructorReviewsCoachRankings';
import FeedInstructorReviewsCoachRankingsByCoach from './FeedInstructorReviewsCoachRankingsByCoach';
import FeedInstructorReviewsStageReviewInfos from './FeedInstructorReviewsStageReviewInfos';
import FeedInstructorReviewsStageCoachReviewInfos from './FeedInstructorReviewsStageCoachReviewInfos';
import FeedInstructorReviewsLowRatingAnswers from './FeedInstructorReviewsLowRatingAnswers';
import FeedInstructorReviewsCoachMembers from './FeedInstructorReviewsCoachMembers';

type Props = {
    onClickAddTrainer?: () => void;
};

function FeedInstructorReviewsPage({ onClickAddTrainer }: Props) {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [selectCoach, setSelectCoach] = useState<{ code: string; value: string } | null>(null);

    // 센터 대시보드 데이터
    const { data: centerData } = useCenterReviewDashboard({
        centerId: authObject?.centerId,
    });

    // 강사 대시보드 데이터
    const { data: coachData } = useCoachReviewDashboard({
        coachId: selectCoach?.code || '',
        centerId: authObject?.centerId,

        enabled: !!selectCoach && selectCoach.value !== '센터 전체',
    });

    // 첫 번째 항목을 기본 선택값으로 설정
    useEffect(() => {
        if (centerData?.centerHeader?.coachList && centerData.centerHeader.coachList.length > 0 && !selectCoach) {
            const firstCoach = centerData.centerHeader.coachList[0];
            setSelectCoach({
                code: firstCoach.id,
                value: firstCoach.name,
            });
        }
    }, [centerData?.centerHeader?.coachList, selectCoach]);

    const isCenterView = selectCoach?.value === '센터 전체';
    const centerHeader = isCenterView ? centerData?.centerHeader : coachData?.centerHeader;

    return (
        <div>
            {/* 날짜 및 드롭다운 */}
            <FeedInstructorReviewsHeader
                centerHeader={centerHeader}
                selectCoach={selectCoach}
                setSelectCoach={setSelectCoach}
            />

            {/* 센터 전체 뷰 */}
            {isCenterView && centerData && (
                <>
                    <Row className="flex w-full gap-[32px] mb-[32px]">
                        <div className="flex flex-col gap-[32px] w-[474px]">
                            {/* 센터 요약 통계 */}
                            <FeedInstructorReviewsCenterSummary centerReviewSummary={centerData.centerReviewSummary} />
                            {/* 강사 순위 */}
                            <FeedInstructorReviewsCoachRankings
                                satisfactionCoachRankings={centerData.satisfactionCoachRankings}
                                onClickAddTrainer={onClickAddTrainer}
                                onSelectCoach={setSelectCoach}
                            />
                        </div>
                        {/* 첫회차, 중간회차, 마지막회차 */}
                        <div className="flex-1">
                            <FeedInstructorReviewsStageReviewInfos stageReviewInfos={centerData.stageReviewInfos} />
                        </div>
                    </Row>
                    {/* 3점이하 리뷰 */}
                    <FeedInstructorReviewsLowRatingAnswers lowRatingAnswers={centerData.lowRatingAnswers} />
                </>
            )}

            {/* 강사 개별 뷰 */}
            {!isCenterView && coachData && (
                <>
                    <Row className="flex w-full gap-[32px] mb-[32px]">
                        <div className="flex flex-col gap-[32px] w-[474px]">
                            <FeedInstructorReviewsCoachSummary coachReviewSummary={coachData.coachReviewSummary} />
                            <FeedInstructorReviewsCoachRankingsByCoach
                                satisfactionCoachRankings={coachData.satisfactionCoachRankings}
                                selectedCoachId={selectCoach?.code || null}
                                onSelectCoach={setSelectCoach}
                            />
                        </div>

                        {/* 첫회차, 중간회차, 마지막회차 */}
                        <div className="flex-1">
                            <FeedInstructorReviewsStageCoachReviewInfos
                                stageCoachReviewInfos={coachData.stageCoachReviewInfos}
                            />
                        </div>
                    </Row>
                    {/* 강사 리뷰 멤버 리스트 */}
                    <FeedInstructorReviewsCoachMembers
                        reviewMembers={coachData.reviewMembers}
                        reviewMemberCount={coachData.reviewMemberCount}
                        totalAnswerCount={coachData.totalAnswerCount}
                    />
                </>
            )}
        </div>
    );
}

export default FeedInstructorReviewsPage;
